import './style.scss';

import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import cn from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import throttle from 'lodash/throttle';
import React, { useCallback, useEffect, useState } from 'react';

import { GeneralSettingsForm, PairSettingsForm } from 'features/arbitrage';
import PlatformIcon from 'shared/components/PlatformIcon';
import { ICexPair, IDexPair } from 'types/pairs';
import { Modal } from 'ui/Modal/Modal';

import { PairsList } from './components';
import {
  EStep,
  IArbitrageForm,
  useAddArbitrageModal,
} from './useAddArbitrageModal/useAddArbitrageModal';

interface IAddPairModalProps {
  onClose: () => void;
  onSuccess?: (form: IArbitrageForm) => void;
  projectId: number;
  mode?: 'add' | 'create';
  cexPairsToAdd?: ICexPair[];
  dexPairsToAdd?: IDexPair[];
}

const variants = {
  hidden: (isForwardDirection: boolean) => ({
    opacity: 0,
    x: isForwardDirection ? 200 : -200,
  }),
  visible: {
    opacity: 1,
    x: 0,
  },
  exit: (isForwardDirection: boolean) => ({
    opacity: 0,
    x: isForwardDirection ? -200 : 200,
  }),
};

const AddArbitrageModal: React.FC<IAddPairModalProps> = ({
  onClose,
  onSuccess,
  projectId,
  mode = 'create',
  cexPairsToAdd,
  dexPairsToAdd,
}) => {
  const {
    project,
    currentStepIdx,
    steps,
    cexPairs,
    dexPairs,
    selectedCexPairs,
    selectedDexPairs,
    currentCexPair,
    currentDexPair,
    currentStepControls,
    setupSteps,
    setSelectedCexPairs,
    setSelectedDexPairs,
    toggleAllPairs,
    generalSettings,
    cexPairSettings,
    dexPairSettings,
  } = useAddArbitrageModal({ onClose, onSuccess, projectId, mode, cexPairsToAdd, dexPairsToAdd });

  const [isForwardDirection, setIsForwardDirection] = useState(true);

  const handlePairSelect = useCallback(
    (pair: ICexPair | IDexPair) => {
      if ('dex' in pair) {
        setSelectedDexPairs(prevPairs => {
          const isSelected = prevPairs.some(selected => selected.id === pair.id);
          if (isSelected) {
            return prevPairs.filter(selected => selected.id !== pair.id);
          } else {
            return [...prevPairs, pair];
          }
        });
      }

      if ('cex' in pair) {
        setSelectedCexPairs(prevPairs => {
          const isSelected = prevPairs.some(selected => selected.id === pair.id);
          if (isSelected) {
            return prevPairs.filter(selected => selected.id !== pair.id);
          } else {
            return [...prevPairs, pair];
          }
        });
      }
    },
    [setSelectedCexPairs, setSelectedDexPairs],
  );

  const handleForwardClick = useCallback(() => {
    if (currentStepControls.forwardControl.disabled) {
      return;
    }

    setIsForwardDirection(true);

    setTimeout(() => {
      currentStepControls.forwardControl?.onForwardClick?.();
    }, 50);
  }, [currentStepControls]);

  const handleBackwardClick = useCallback(() => {
    setIsForwardDirection(false);

    setTimeout(() => {
      currentStepControls.backwardControl?.onBackwardClick?.();
    }, 50);
  }, [currentStepControls]);

  useEffect(() => {
    setupSteps({ cexPairsCount: selectedCexPairs.length, dexPairsCount: selectedDexPairs.length });
  }, [selectedCexPairs.length, selectedDexPairs.length, setupSteps]);

  const step = steps[currentStepIdx];

  if (!project || !step) return null;

  return (
    <Modal
      customHeader={
        <div className="mm-add-arbitrage-modal__header">
          {[EStep.cex_pairs, EStep.dex_pairs].includes(step.name) ? (
            step.name === EStep.cex_pairs ? (
              <span>CEX pairs</span>
            ) : (
              <span>DEX pairs</span>
            )
          ) : step.name === EStep.general ? (
            <span>General settings</span>
          ) : currentCexPair ? (
            <>
              <PlatformIcon
                platform={currentCexPair.cex}
                isIcon
                className="mm-sidebar__projects__project__trading-pair-icon"
              />
              {currentCexPair.symbol}
            </>
          ) : currentDexPair ? (
            <>
              <PlatformIcon
                platform={currentDexPair.dex}
                isIcon
                className="mm-sidebar__projects__project__trading-pair-icon"
              />
              {currentDexPair.symbol}
            </>
          ) : undefined}
        </div>
      }
      onClose={onClose}
      customButtons={
        <div className="step-controls-container">
          <Button
            size="middle"
            icon={<LeftOutlined />}
            className={cn('step-control-button', {
              _hidden: !currentStepControls.backwardControl?.title,
            })}
            onClick={throttle(handleBackwardClick, 500)}
          >
            {currentStepControls.backwardControl?.title}
          </Button>
          <Button
            type="primary"
            size="middle"
            icon={<RightOutlined />}
            iconPlacement="end"
            className={cn('step-control-button', {
              _disabled: currentStepControls.forwardControl.disabled,
            })}
            onClick={handleForwardClick}
            disabled={currentStepControls.forwardControl.disabled}
          >
            {currentStepControls.forwardControl.title}
          </Button>
        </div>
      }
      className="mm-add-arbitrage-modal-wrapper"
    >
      <div className="mm-add-arbitrage-modal">
        <div className="stepper-form-content scrollable">
          <AnimatePresence custom={isForwardDirection} mode="wait">
            <motion.div
              key={currentStepIdx}
              custom={isForwardDirection}
              variants={variants}
              initial="hidden"
              animate="visible"
              exit="exit"
              transition={{ duration: 0.2 }}
              className="form-step"
            >
              <div className="mm-add-arbitrage-modal__content">
                {step.name === EStep.cex_pairs && (
                  <PairsList
                    type="cex"
                    pairs={cexPairs}
                    toggleAllPairs={() => toggleAllPairs('cex')}
                    selectedPairs={selectedCexPairs}
                    handlePairSelect={handlePairSelect}
                  />
                )}
                {step.name === EStep.dex_pairs && (
                  <PairsList
                    type="dex"
                    pairs={dexPairs}
                    toggleAllPairs={() => toggleAllPairs('dex')}
                    selectedPairs={selectedDexPairs}
                    handlePairSelect={handlePairSelect}
                  />
                )}
                {step.name === EStep.cex_pair && currentCexPair && (
                  <PairSettingsForm
                    key={currentCexPair.id}
                    cexPair={currentCexPair}
                    errors={cexPairSettings.errors}
                    setFieldTouched={cexPairSettings.setFieldTouched}
                    setFieldValue={cexPairSettings.setFieldValue}
                    touched={cexPairSettings.touched}
                    values={cexPairSettings.values}
                  />
                )}
                {step.name === EStep.dex_pair && currentDexPair && (
                  <PairSettingsForm
                    key={currentDexPair.id}
                    dexPair={currentDexPair}
                    errors={dexPairSettings.errors}
                    setFieldTouched={dexPairSettings.setFieldTouched}
                    setFieldValue={dexPairSettings.setFieldValue}
                    touched={dexPairSettings.touched}
                    values={dexPairSettings.values}
                  />
                )}
                {step.name === EStep.general && (
                  <GeneralSettingsForm
                    values={generalSettings.values}
                    setFieldValue={generalSettings.setFieldValue}
                    errors={generalSettings.errors}
                    touched={generalSettings.touched}
                    setFieldTouched={generalSettings.setFieldTouched}
                  />
                )}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </Modal>
  );
};

export { AddArbitrageModal };
